<template>
  <div v-if="variant === 'card'" class="ai-navigation__card-top">
    <AiNavigationCardHeader :item="item" :show-cta="showCta" />
  </div>

  <div
    v-if="variant === 'card'"
    class="ai-navigation__card-bottom"
    role="button"
    tabindex="0"
    @click="emit('detail')"
    @keydown.enter="emit('detail')"
  >
    <div v-if="item.thumbnail_url" :class="thumbClass">
      <img :src="item.thumbnail_url" :alt="item.title" loading="lazy" draggable="false" />
    </div>

    <div :class="contentClass">
      <span :class="titleClass">{{ item.title }}</span>
      <p v-if="displayText" :class="textClass">{{ displayText }}</p>
    </div>
  </div>

  <template v-else-if="variant === 'popup'">
    <div v-if="item.thumbnail_url" :class="thumbClass">
      <img :src="item.thumbnail_url" :alt="item.title" :loading="undefined" draggable="false" />
    </div>

    <div :class="contentClass">
      <AiNavigationCardHeader :item="item" :show-cta="showCta" />

      <span :class="titleClass">{{ item.title }}</span>

      <p v-if="displayText" :class="textClass">{{ displayText }}</p>
    </div>
  </template>

  <!-- detail: ヘッダー(ラベル+CTA)はモーダル側で上部固定するため含めない -->
  <template v-else>
    <div v-if="item.thumbnail_url" class="ai-navigation__card-detail-thumb">
      <img :src="item.thumbnail_url" :alt="item.title" draggable="false" />
    </div>

    <span class="ai-navigation__card-popup-title">{{ item.title }}</span>

    <p v-if="displayText" class="ai-navigation__card-popup-text">{{ displayText }}</p>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AiNavigationCardHeader from './AiNavigationCardHeader.vue';
import type { SearchItem } from './useCardUtils';
import { normalizeExcerpt } from './useCardUtils';

interface Props {
  item: SearchItem;
  variant: 'card' | 'popup' | 'detail';
  showCta?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showCta: true,
});

const emit = defineEmits<{ detail: [] }>();

const thumbClass = computed(() =>
  props.variant === 'card'
    ? 'ai-navigation__card-thumb'
    : 'ai-navigation__card-popup-thumb'
);

const contentClass = computed(() =>
  props.variant === 'card'
    ? 'ai-navigation__card-content'
    : 'ai-navigation__card-popup-content'
);

const titleClass = computed(() =>
  props.variant === 'card'
    ? 'ai-navigation__card-title'
    : 'ai-navigation__card-popup-title'
);

const textClass = computed(() =>
  props.variant === 'card'
    ? 'ai-navigation__card-excerpt'
    : 'ai-navigation__card-popup-text'
);

const displayText = computed(() =>
  normalizeExcerpt(props.item.long_excerpt || props.item.feature || props.item.excerpt || '')
);
</script>

<style scoped>
.ai-navigation__card-top {
  flex-shrink: 0;
  padding: 10px 12px 0;
}

.ai-navigation__card-bottom {
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  cursor: pointer;
}

.ai-navigation__card-thumb {
  width: 30%;
  flex-shrink: 0;
  align-self: stretch;
  overflow: hidden;
}

.ai-navigation__card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
  display: block;
}

.ai-navigation__card-popup-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
  display: block;
}

.ai-navigation__card-detail-thumb {
  width: 100%;
  height: 160px;
  flex-shrink: 0;
}

.ai-navigation__card-detail-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
  display: block;
}

.ai-navigation__card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px 14px;
  overflow: hidden;
  min-height: 0;
}


.ai-navigation__card-title {
  font-size: 18px;
  font-weight: 600;
  color: #212121;
  text-decoration: none;
  line-height: 1.55;
  display: block;
}

.ai-navigation__card-title:hover {
  color: #00A0E9;
  text-decoration: underline;
}

.ai-navigation__card-excerpt {
  font-size: 14px;
  color: #6b7a8f;
  line-height: 1.6;
  margin: 0;
}


.ai-navigation__card-popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-navigation__card-popup-title {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
  text-decoration: none;
  line-height: 1.55;
  display: block;
}

.ai-navigation__card-popup-title:hover {
  color: #00A0E9;
  text-decoration: underline;
}

.ai-navigation__card-popup-text {
  font-size: 12px;
  color: #4a5a72;
  line-height: 1.65;
  margin: 0;
  flex: 1;
  white-space: pre-line;
  word-break: break-word;
}

/* SP（タブレット含む）: カードのフォントを一回り小さくする */
@media (max-width: 1023px) {
  .ai-navigation__card-title {
    font-size: 16px;
  }

  .ai-navigation__card-excerpt {
    font-size: 13px;
  }
}
</style>
